/* eslint-disable react/no-unknown-property */
/* ============================================================
   AirlinerMesh – twin-jet narrow-body airliner
   - Lathe fuselage sampled uniformly in Z so the procedural
     livery (windows, doors, cockpit glazing, cheat lines,
     titles) wraps correctly
   - Swept, tapered wings with dihedral, winglets and a belly
     fairing; swept tail plane and fin
   - Nacelles with spinning fan, spinner cone and hot exhaust
   - Navigation lights + blinking anti-collision beacon
   Nose points -Z, tail +Z. Engines sit at (±3.5, -0.75, 0.4)
   so the contrail emitters in Airplane() still line up.
   ============================================================ */
import { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { getLiveryTexture } from "@/components/world/textures";

/* ---------- geometry (module-level, shared) ---------- */
const FUSELAGE_GEO = (() => {
  const zNose = -5.6;
  const zTail = 6.2;
  const N = 60;
  const pts = [];
  for (let i = 0; i <= N; i++) {
    const z = zNose + (i / N) * (zTail - zNose);
    let r;
    if (z < -3.4) {
      const s = (-3.4 - z) / 2.2; // 0 at body → 1 at nose tip
      r = 0.92 * Math.sqrt(Math.max(0, 1 - s * s)) * (1 - 0.08 * s);
    } else if (z < 2.6) {
      r = 0.92;
    } else {
      const s = Math.min(1, Math.max(0, (z - 2.6) / 3.6));
      r = 0.92 * Math.pow(1 - s, 1.25) + 0.05 * (1 - s);
    }
    pts.push(new THREE.Vector2(Math.max(0.001, r), z));
  }
  const geo = new THREE.LatheGeometry(pts, 40);
  geo.rotateX(Math.PI / 2); // lathe Y → Z (belly at u=0, right at u=0.25, top at u=0.5)
  // Tail cone upsweep
  const pos = geo.attributes.position;
  for (let i = 0; i < pos.count; i++) {
    const z = pos.getZ(i);
    if (z > 2.6) {
      const s = (z - 2.6) / 3.6;
      pos.setY(i, pos.getY(i) + s * s * 0.8);
    }
  }
  geo.computeVertexNormals();
  return geo;
})();

const WING_GEO = (() => {
  const s = new THREE.Shape();
  // x = span outward, y = chord (positive = aft)
  s.moveTo(0, -1.75);
  s.lineTo(6.4, 0.95);
  s.lineTo(6.4, 1.7);
  s.lineTo(2.3, 1.9);
  s.lineTo(0, 2.1);
  s.lineTo(0, -1.75);
  const geo = new THREE.ExtrudeGeometry(s, {
    depth: 0.16,
    bevelEnabled: true,
    bevelThickness: 0.05,
    bevelSize: 0.07,
    bevelSegments: 2,
    steps: 1,
  });
  geo.rotateX(Math.PI / 2); // shape y (aft) → +z, thickness → -y
  geo.translate(0, 0.08, 0);
  geo.computeVertexNormals();
  return geo;
})();

const HSTAB_GEO = (() => {
  const s = new THREE.Shape();
  s.moveTo(0, -0.25);
  s.lineTo(2.5, 0.65);
  s.lineTo(2.5, 1.05);
  s.lineTo(0, 1.15);
  s.lineTo(0, -0.25);
  const geo = new THREE.ExtrudeGeometry(s, { depth: 0.09, bevelEnabled: true, bevelThickness: 0.03, bevelSize: 0.04, bevelSegments: 1 });
  geo.rotateX(Math.PI / 2);
  geo.translate(0, 0.045, 0);
  geo.computeVertexNormals();
  return geo;
})();

/* vertical fin: (chord along z, height along y), thickness along x */
function finGeometry(rootChord, tipChord, height, sweep, thickness) {
  const s = new THREE.Shape();
  s.moveTo(0, 0);
  s.lineTo(rootChord, 0);
  s.lineTo(sweep + tipChord, height);
  s.lineTo(sweep, height);
  s.lineTo(0, 0);
  const geo = new THREE.ExtrudeGeometry(s, { depth: thickness, bevelEnabled: true, bevelThickness: 0.02, bevelSize: 0.03, bevelSegments: 1 });
  geo.rotateY(-Math.PI / 2); // shape x → z
  geo.translate(thickness / 2, 0, 0);
  geo.computeVertexNormals();
  return geo;
}
const VFIN_GEO = finGeometry(2.7, 1.0, 3.1, 1.75, 0.14);
const WINGLET_GEO = finGeometry(0.72, 0.22, 0.62, 0.42, 0.05);

const NACELLE_GEO = (() => {
  const pts = [
    [0.5, -1.0],
    [0.54, -0.85],
    [0.56, -0.5],
    [0.55, 0.1],
    [0.5, 0.6],
    [0.44, 0.95],
    [0.4, 1.0],
  ].map(([r, z]) => new THREE.Vector2(r, z));
  const geo = new THREE.LatheGeometry(pts, 28);
  geo.rotateX(Math.PI / 2);
  geo.computeVertexNormals();
  return geo;
})();
const NACELLE_INNER_GEO = (() => {
  const pts = [
    [0.46, -1.0],
    [0.45, -0.3],
    [0.3, 0.3],
    [0.001, 0.32],
  ].map(([r, z]) => new THREE.Vector2(r, z));
  const geo = new THREE.LatheGeometry(pts, 24);
  geo.rotateX(Math.PI / 2);
  geo.computeVertexNormals();
  return geo;
})();
const FAN_BLADE_GEO = new THREE.BoxGeometry(0.07, 0.36, 0.012);
const BELLY_GEO = (() => {
  const g = new THREE.SphereGeometry(1, 20, 12);
  g.scale(1.25, 0.55, 2.9);
  return g;
})();

/* ---------- materials ---------- */
const MAT = {
  wing: new THREE.MeshStandardMaterial({ color: "#E6E1D8", metalness: 0.45, roughness: 0.38 }),
  wingDark: new THREE.MeshStandardMaterial({ color: "#7D7A76", metalness: 0.6, roughness: 0.4 }),
  fin: new THREE.MeshStandardMaterial({ color: "#E94B4B", metalness: 0.35, roughness: 0.4 }),
  finAccent: new THREE.MeshStandardMaterial({ color: "#6B1F47", metalness: 0.35, roughness: 0.4 }),
  nacelle: new THREE.MeshStandardMaterial({ color: "#EDE9E2", metalness: 0.5, roughness: 0.3 }),
  nacelleInner: new THREE.MeshStandardMaterial({ color: "#1B1E23", metalness: 0.85, roughness: 0.35, side: THREE.BackSide }),
  exhaust: new THREE.MeshStandardMaterial({ color: "#5A5350", metalness: 0.95, roughness: 0.3 }),
  blade: new THREE.MeshStandardMaterial({ color: "#B9BCC2", metalness: 0.95, roughness: 0.25, side: THREE.DoubleSide }),
  spinner: new THREE.MeshStandardMaterial({ color: "#D8D5CF", metalness: 0.9, roughness: 0.3 }),
  belly: new THREE.MeshStandardMaterial({ color: "#B9B7B3", metalness: 0.4, roughness: 0.45 }),
  antenna: new THREE.MeshStandardMaterial({ color: "#F0ECE6", roughness: 0.6 }),
  navRed: new THREE.MeshBasicMaterial({ color: "#FF3B3B", toneMapped: false }),
  navGreen: new THREE.MeshBasicMaterial({ color: "#3BFF7A", toneMapped: false }),
  strobe: new THREE.MeshBasicMaterial({ color: "#FFFFFF", toneMapped: false }),
};

function Engine({ x, fanRef }) {
  return (
    <group position={[x, -0.75, 0.4]}>
      <mesh geometry={NACELLE_GEO} material={MAT.nacelle} />
      <mesh geometry={NACELLE_INNER_GEO} material={MAT.nacelleInner} />
      {/* intake lip ring */}
      <mesh position={[0, 0, -1.0]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.5, 0.045, 10, 28]} />
        <meshStandardMaterial color="#C9C6C0" metalness={0.95} roughness={0.2} />
      </mesh>
      {/* fan */}
      <group ref={fanRef} position={[0, 0, -0.72]}>
        {[...Array(14)].map((_, i) => (
          <group key={i} rotation={[0, 0, (i / 14) * Math.PI * 2]}>
            <mesh geometry={FAN_BLADE_GEO} material={MAT.blade} position={[0, 0.25, 0]} rotation={[0, 0.6, 0]} />
          </group>
        ))}
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, -0.08]} material={MAT.spinner}>
          <coneGeometry args={[0.11, 0.3, 12]} />
        </mesh>
      </group>
      {/* core exhaust */}
      <mesh position={[0, 0, 1.15]} rotation={[Math.PI / 2, 0, 0]} material={MAT.exhaust}>
        <cylinderGeometry args={[0.22, 0.3, 0.45, 18, 1, true]} />
      </mesh>
      <mesh position={[0, 0, 1.35]} rotation={[Math.PI / 2, 0, 0]} material={MAT.exhaust}>
        <coneGeometry args={[0.12, 0.35, 12]} />
      </mesh>
      {/* pylon */}
      <mesh position={[0, 0.55, 0.15]} rotation={[0.15, 0, 0]} material={MAT.wingDark}>
        <boxGeometry args={[0.2, 0.55, 1.5]} />
      </mesh>
    </group>
  );
}

export function AirlinerMesh({ propRef }) {
  const fanL = useRef();
  const beaconTop = useRef();
  const beaconBottom = useRef();
  const strobe = useRef();
  const livery = useMemo(() => getLiveryTexture(), []);
  const fuselageMat = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        map: livery || undefined,
        color: livery ? "#FFFFFF" : "#F4EADE",
        metalness: 0.35,
        roughness: 0.32,
      }),
    [livery]
  );

  useFrame((state, delta) => {
    const t = state.clock.elapsedTime;
    if (fanL.current && propRef?.current) fanL.current.rotation.z = -propRef.current.rotation.z;
    else if (fanL.current) fanL.current.rotation.z -= delta * 40;
    const beaconOn = (t * 1.2) % 1 < 0.12;
    if (beaconTop.current) beaconTop.current.visible = beaconOn;
    if (beaconBottom.current) beaconBottom.current.visible = beaconOn;
    if (strobe.current) strobe.current.visible = (t * 1.5) % 1 < 0.06 || ((t * 1.5) % 1 > 0.14 && (t * 1.5) % 1 < 0.2);
  });

  return (
    <group scale={0.75}>
      {/* fuselage */}
      <mesh geometry={FUSELAGE_GEO} material={fuselageMat} castShadow />
      {/* belly fairing at the wing root */}
      <mesh geometry={BELLY_GEO} material={MAT.belly} position={[0, -0.5, 0.45]} />
      {/* wings */}
      <group position={[0, -0.32, 0.25]} rotation={[0, 0, 0.09]}>
        <mesh geometry={WING_GEO} material={MAT.wing} castShadow />
        <mesh geometry={WINGLET_GEO} material={MAT.fin} position={[6.34, 0.05, 1.0]} rotation={[0, 0, -0.55]} />
        {/* green nav light (starboard) */}
        <mesh position={[6.42, 0.1, 0.98]} material={MAT.navGreen}>
          <sphereGeometry args={[0.07, 8, 8]} />
        </mesh>
      </group>
      <group position={[0, -0.32, 0.25]} rotation={[0, 0, -0.09]}>
        <mesh geometry={WING_GEO} material={MAT.wing} scale={[-1, 1, 1]} castShadow />
        <mesh geometry={WINGLET_GEO} material={MAT.fin} position={[-6.34, 0.05, 1.0]} rotation={[0, 0, 0.55]} />
        {/* red nav light (port) */}
        <mesh position={[-6.42, 0.1, 0.98]} material={MAT.navRed}>
          <sphereGeometry args={[0.07, 8, 8]} />
        </mesh>
      </group>
      {/* flap track fairings */}
      {[-3.6, -2.2, 2.2, 3.6].map((fx, i) => (
        <mesh key={i} position={[fx, -0.5, 1.75 + Math.abs(fx) * 0.02]} rotation={[Math.PI / 2, 0, 0]} material={MAT.wingDark}>
          <capsuleGeometry args={[0.07, 0.6, 3, 8]} />
        </mesh>
      ))}
      {/* engines */}
      <Engine x={3.5} fanRef={propRef} />
      <Engine x={-3.5} fanRef={fanL} />
      {/* tail plane */}
      <group position={[0, 0.55, 4.6]} rotation={[0, 0, 0.1]}>
        <mesh geometry={HSTAB_GEO} material={MAT.wing} />
      </group>
      <group position={[0, 0.55, 4.6]} rotation={[0, 0, -0.1]}>
        <mesh geometry={HSTAB_GEO} material={MAT.wing} scale={[-1, 1, 1]} />
      </group>
      {/* vertical fin + rudder accent */}
      <mesh geometry={VFIN_GEO} material={MAT.fin} position={[0, 0.7, 3.6]} />
      <mesh position={[0, 2.35, 5.45]} rotation={[0.55, 0, 0]} material={MAT.finAccent}>
        <boxGeometry args={[0.16, 1.1, 0.35]} />
      </mesh>
      {/* logo disc on fin */}
      {[0.09, -0.09].map((ox, i) => (
        <mesh key={i} position={[ox, 2.3, 5.05]} rotation={[0, Math.PI / 2, 0]}>
          <circleGeometry args={[0.46, 20]} />
          <meshStandardMaterial color="#F4EADE" roughness={0.5} side={THREE.DoubleSide} />
        </mesh>
      ))}
      {/* white tail strobe */}
      <mesh ref={strobe} position={[0, 0.85, 6.25]} material={MAT.strobe}>
        <sphereGeometry args={[0.09, 8, 8]} />
      </mesh>
      {/* anti-collision beacons */}
      <mesh ref={beaconTop} position={[0, 0.96, 0.9]} material={MAT.navRed}>
        <sphereGeometry args={[0.08, 8, 8]} />
      </mesh>
      <mesh ref={beaconBottom} position={[0, -0.98, -1.4]} material={MAT.navRed}>
        <sphereGeometry args={[0.08, 8, 8]} />
      </mesh>
      {/* antennas & sensors */}
      <mesh position={[0, 0.98, -1.6]} material={MAT.antenna}>
        <boxGeometry args={[0.05, 0.22, 0.5]} />
      </mesh>
      <mesh position={[0, 0.98, 2.2]} material={MAT.antenna}>
        <boxGeometry args={[0.05, 0.18, 0.4]} />
      </mesh>
      {/* landing light in nose */}
      <mesh position={[0, -0.55, -5.15]}>
        <sphereGeometry args={[0.1, 8, 8]} />
        <meshBasicMaterial color="#FFF7D6" toneMapped={false} />
      </mesh>
    </group>
  );
}
